import { useEffect, useRef, useState } from "react";

/**
 * The altimeter on the right edge of the landing page.
 *
 * SectionMark tells you where you are once per section. This is the same
 * information, always on screen: a dot per section on a thin vertical track
 * and a small sun that climbs it as you read. The first section sits at the
 * BOTTOM of the track and the last at the top, so the sun rises.
 *
 * It is a navigation aid, not a scroll controller: nothing snaps or hijacks
 * the wheel. That is also why a click does not set the active dot itself —
 * the scroll it starts passes real sections, and letting the observer report
 * them is what makes the sun travel instead of teleport.
 *
 * Sibling of `components/SectionRail` (Profile). That one lives on app paper;
 * this one floats over night and paper alike, so it brings its own paper.
 */

// SectionMark's paper tones, so rail and eyebrow ticks agree on what "lit",
// "passed" and "ahead" look like. Dawn is reserved for the sun: dawn is light.
// `on` is stone-900 ink, not dawn: the sun sits on the active dot and is the
// rail's only light. `next` is a step darker than SectionMark's sand so the
// upcoming stops still read on the paper capsule.
const TONE = { on: "#1c1917", past: "#c2b8a6", next: "#cfc4b1" };
// Each dot owns a 20px slot; fixed geometry means no measuring before paint.
const SLOT = 20;
// Ten steps, so the observer keeps reporting as a section scrolls through.
const THRESHOLDS = Array.from({ length: 11 }, (_, i) => i / 10);

const reducedMotion = () =>
  Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches);

export default function SectionRail({ sections = [] }) {
  const [resolved, setResolved] = useState([]);
  const [active, setActive] = useState(null);
  // scrollY 0 is "before the first section": the hero. No dot is lit and the
  // sun waits at the horizon, which is more honest than lighting section one
  // while the reader is still above it.
  const [atTop, setAtTop] = useState(() => window.scrollY === 0);
  const seen = useRef(new Map());
  const buttons = useRef([]);

  // Callers pass an inline literal, a new reference every render; keying the
  // effect on the ids keeps it from re-subscribing the observer each frame.
  const key = sections.map((s) => s.id).join("|");
  useEffect(() => {
    const found = sections.filter((s) => document.getElementById(s.id));
    setResolved(found);
    setActive(found[0]?.id ?? null);
    seen.current = new Map();
    if (found.length < 2) return undefined;
    // No observer (jsdom, an old webview): the rail still renders and still
    // navigates on click; only the "where am I" highlight stays on the first.
    if (typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver((entries) => {
      // Visible pixels, not ratio: a short section fully on screen would
      // otherwise outrank the tall one the reader is actually in.
      for (const e of entries) {
        const px = e.isIntersecting ? e.intersectionRatio * (e.boundingClientRect?.height || 1) : 0;
        seen.current.set(e.target.id, px);
      }
      let best = null;
      let bestPx = 0;
      for (const s of found) {
        const px = seen.current.get(s.id) || 0;
        // `>=` so a tie goes to the later section — the one being scrolled into.
        if (px > 0 && px >= bestPx) { best = s.id; bestPx = px; }
      }
      // Nothing visible (footer, hero): keep the last answer rather than blink.
      if (best) setActive(best);
    }, { threshold: THRESHOLDS });
    found.forEach((s) => observer.observe(document.getElementById(s.id)));

    const onScroll = () => setAtTop(window.scrollY === 0);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const n = resolved.length;
  // One section is not navigation.
  if (n < 2) return null;

  const activeIdx = atTop ? -1 : resolved.findIndex((s) => s.id === active);
  const progress = Math.max(0, activeIdx) / (n - 1);

  const jump = (i) => {
    const el = document.getElementById(resolved[i].id);
    if (!el) return;
    el.scrollIntoView({ behavior: reducedMotion() ? "auto" : "smooth", block: "start" });
    // Reflect the section in the URL without the hash-jump a location change
    // would cause on top of the scroll already in flight.
    window.history.replaceState(null, "", `#${resolved[i].id}`);
  };

  // Lives on the nav, so it only ever sees keys from a focused dot; j/k and the
  // arrows keep their normal meaning everywhere else on the page.
  const onKeyDown = (e) => {
    const from = buttons.current.indexOf(e.target);
    const step = { ArrowDown: 1, j: 1, ArrowUp: -1, k: -1 }[e.key];
    if (from < 0 || !step) return;
    e.preventDefault();
    const to = Math.min(n - 1, Math.max(0, from + step));
    if (to === from) return;
    jump(to);
    buttons.current[to]?.focus();
  };

  return (
    <nav
      aria-label="Sections"
      data-testid="section-rail"
      onKeyDown={onKeyDown}
      // z-40 sits with BackToTop, under PublicNav (z-[60]). Paper, not white:
      // white/70 over a night section goes office-grey; paper stays warm.
      className="hidden lg:flex fixed right-5 top-1/2 -translate-y-1/2 z-40 flex-col rounded-md border border-stone-950/15 bg-paper/85 backdrop-blur-md shadow-sm px-1.5 py-2"
    >
      {/* Reversed so DOM (and Tab) order is reading order while the first
          section still sits at the foot of the track. */}
      {/* Reading order top to bottom, like every scrollbar. The first cut
          ran section one from the foot of the track so the sun "rose" as
          you read; it read as a scrollbar going the wrong way. The sun
          still carries the light — it just travels down with the reader. */}
      <div className="relative flex flex-col" style={{ height: n * SLOT }}>
        {resolved.map((s, i) => {
          const state = i === activeIdx ? "on" : i < activeIdx ? "past" : "next";
          return (
            <button
              key={s.id}
              ref={(el) => { buttons.current[i] = el; }}
              type="button"
              onClick={() => jump(i)}
              aria-label={s.label}
              aria-current={i === activeIdx ? "true" : undefined}
              data-testid={`rail-dot-${s.id}`}
              className="group relative grid h-5 w-5 place-items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-night-lift"
            >
              <span
                aria-hidden="true"
                className="h-2 w-2 rounded-full transition-colors"
                style={{ backgroundColor: TONE[state] }}
              />
              {/* Label on hover/focus. No `title`: keyboards never see it. */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute right-full top-1/2 mr-3 -translate-y-1/2 whitespace-nowrap rounded-sm bg-night-lift px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
              >
                {s.label}
              </span>
            </button>
          );
        })}
        {/* The sun. Below the horizon (nothing active) it dims rather than
            hides. Motion is CSS-only, so reduced-motion needs no listener. */}
        <span
          aria-hidden="true"
          data-testid="rail-sun"
          data-progress={progress.toFixed(2)}
          className={`pointer-events-none absolute left-1 top-1 h-3 w-3 rounded-full bg-dawn-500 ring-1 ring-dawn-300/80 shadow-[0_0_12px_2px_rgba(255,143,94,0.55)] motion-safe:transition-[transform,opacity] motion-safe:duration-500 motion-safe:ease-out ${activeIdx < 0 ? "opacity-60" : "opacity-100"}`}
          style={{ transform: `translateY(${progress * (n - 1) * SLOT}px)` }}
        />
      </div>
    </nav>
  );
}
